import React, { useState, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  fetchNewMaterialCategories,
  fetchMaterialRequestCategories,
  fetchQuantityTypes,
  fetchRegions,
  API_BASE_URL,
} from '../services/api';
import './PageLayout.css';
import './MaterialRequestsPage.css';
import { logAction } from '../services/api';
import { auth } from '../firebaseConfig';
import { useRegion } from '../context/RegionContext';
import { useUserRole } from '../hooks/useUserRole';
import StyledDropdown from '../components/StyledDropdown';
import VisibilityToggle from '../components/VisibilityToggle';

// Move SuccessModal component outside - define it BEFORE MaterialRequestsPage
// Replace your existing SuccessModal with this:
const SuccessModal = ({ isOpen, onClose, requestId, visibility }) => {
  if (!isOpen) return null;
  const isPrivate = visibility === 'private';

  return (
    <div className="success-modal-overlay" onClick={onClose}>
      <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="success-modal-header">
          <div className="success-modal-icon">{isPrivate ? '🔒' : '🎉'}</div>
          <h2>Material Request Submitted!</h2>
        </div>
        <div className="success-modal-body">
          <p>
            {isPrivate
              ? 'Your private material request has been submitted for admin review. Once approved, it will only be visible to your account.'
              : 'Your material request has been submitted for admin review. Once approved, it will be available to all users in your region.'}
          </p>
          <div className="request-details">
            <p><strong>Request ID:</strong> <code>{requestId}</code></p>
            <p>An administrator will review your submission and you'll be notified via email once it's processed.</p>
          </div>
        </div>
        <div className="success-modal-footer">
          <button className="success-modal-button" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
};


function MaterialRequestsPage() {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedRequestId, setSubmittedRequestId] = useState('');
  const { selectedRegion } = useRegion();
  const { role } = useUserRole();
  
  // Region and form states
  const [regions, setRegions] = useState([]);
  const [quantityTypes, setQuantityTypes] = useState([]);
  const [materialCategories, setMaterialCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Visability
  const [visibility, setVisibility] = useState('public')

  const regionName = useMemo(() => {
    const r = regions.find(x => x.id === selectedRegion);
    return r ? r.name : selectedRegion;
  }, [regions, selectedRegion]);

  // Material form state
  const [materialForm, setMaterialForm] = useState({
    Id: uuidv4(),
    Name: '',
    Description: '',
    ColorHex: '#000000',
    QuantityType: '',
    CategoryId: '',
  });

  // Environmental impact values based on quantity type
  const [environmentalData, setEnvironmentalData] = useState({
    carbon: { value: '', unit: '' },
    biogenic: { value: '', unit: '' },
    weight: { value: '', unit: '' },
    cost: { value: '', unit: '' }
  });

  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Remove successMessage state as it's not used anymore

  // Debugging effect
  useEffect(() => {
    console.log('🔍 Role hook debug:');
    console.log('Role value:', role);
    console.log('Role type:', typeof role);
    console.log('Auth user:', auth.currentUser);
    
    if (auth.currentUser) {
      auth.currentUser.getIdTokenResult().then(tokenResult => {
        console.log('🔍 Firebase token claims:', tokenResult.claims);
        console.log('🔍 Role from claims:', tokenResult.claims.role);
      }).catch(error => {
        console.error('🔍 Error getting token claims:', error);
      });
    }
  }, [role, auth.currentUser]);

  // Load regions once
  useEffect(() => {
    (async () => {
      try {
        const rs = await fetchRegions();
        setRegions(rs);
      } catch (e) {
        console.error('Error loading regions', e);
      }
    })();
  }, []);

  // Load all data when region changes
  useEffect(() => {
    if (!selectedRegion) return;
    loadAllData();
  }, [selectedRegion]);

  // Update environmental data units when quantity type changes
  useEffect(() => {
    if (materialForm.QuantityType) {
      const selectedType = quantityTypes.find(qt => qt.id === materialForm.QuantityType);
      const unitName = selectedType ? selectedType.name : '';
      
      setEnvironmentalData(prev => ({
        carbon: { ...prev.carbon, unit: `kg CO2e per ${unitName}` },
        biogenic: { ...prev.biogenic, unit: `kg CO2e per ${unitName}` },
        weight: { ...prev.weight, unit: `kg per ${unitName}` },
        cost: { ...prev.cost, unit: `$ per ${unitName}` }
      }));
    }
  }, [materialForm.QuantityType, quantityTypes]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadQuantityTypes(),
        loadMaterialCategories()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQuantityTypes = async () => {
  try {
    const data = await fetchQuantityTypes(); // Already returns array directly
    const parsedData = Array.isArray(data) ? data : [];
    console.log('🔍 Parsed quantity types:', parsedData);
    setQuantityTypes(parsedData);
  } catch (error) {
    console.error('Error fetching quantity types:', error);
    setQuantityTypes([]);
  }
};

const loadMaterialCategories = async () => {
  try {
    const response = await fetchMaterialRequestCategories();
    setMaterialCategories(Array.isArray(response.data.data) ? response.data.data : []);
  } catch (error) {
    console.error('Error fetching categories:', error);
    setMaterialCategories([]);
  }
};

  // Material form handlers
  const handleMaterialInputChange = (e) => {
    const { name, value } = e.target;
    setMaterialForm({ ...materialForm, [name]: value });
  };

  const generateNewGUID = () => {
    setMaterialForm({ ...materialForm, Id: uuidv4() });
  };

  // Environmental data handlers
  const handleEnvironmentalChange = (category, value) => {
    setEnvironmentalData(prev => ({
      ...prev,
      [category]: { ...prev[category], value }
    }));
  };

  // File upload handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
  const fileArray = Array.from(files);
  
  console.log('📂 Processing files:', fileArray.map(f => ({
    name: f.name,
    size: f.size,
    type: f.type
  })));
  
  // Validate file types and sizes
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/jpg', // Added jpg separately
    'image/png',
    'image/gif',
    'text/plain'
  ];
  
  const maxSize = 10 * 1024 * 1024; // 10MB
  
  // Process files sequentially to avoid overwhelming the server
  for (const file of fileArray) {
    if (!allowedTypes.includes(file.type)) {
      console.warn('❌ File type not supported:', file.type, 'for file:', file.name);
      alert(`File type not supported: ${file.name} (${file.type})`);
      continue;
    }
    
    if (file.size > maxSize) {
      console.warn('❌ File too large:', file.size, 'for file:', file.name);
      alert(`File too large (max 10MB): ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
      continue;
    }
    
    // Add small delay between uploads to prevent server overload
    if (fileArray.indexOf(file) > 0) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    await uploadFile(file);
  }
};

// DEBUGGING: Add this temporary debug function to your component
const debugUploadEnvironment = () => {
  console.log('🔍 Upload Environment Debug:', {
    apiBaseUrl: API_BASE_URL,
    selectedRegion,
    role,
    authUser: auth.currentUser?.uid,
    authEmail: auth.currentUser?.email,
    materialFormId: materialForm.Id,
    userAgent: navigator.userAgent,
    origin: window.location.origin
  });
};

const uploadFile = async (file) => {
  setUploading(true);
  
  try {
    console.log('📤 Starting file upload:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      requestId: materialForm.Id,
      regionId: selectedRegion,
      userRole: role
    });

    // ✅ GET FIREBASE TOKEN FIRST
    const token = await auth.currentUser.getIdToken();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('requestId', materialForm.Id);
    formData.append('regionId', selectedRegion);
    
    // Enhanced fetch with better error handling
    const response = await fetch(`${API_BASE_URL}/material-requests/upload`, {
      method: 'POST',
      headers: {
        // Don't set Content-Type for FormData - let browser set it with boundary
        'Authorization': `Bearer ${token}`,
        'x-region': selectedRegion,
        'X-User-ID': auth.currentUser?.uid || 'unknown',
        'X-User-Email': auth.currentUser?.email || 'unknown',
        'X-User-Role': role || 'supplier', // Ensure role is always sent
      },
      body: formData,
    });

    console.log('📥 Upload response status:', response.status);

    // Enhanced response handling
    let result;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      result = await response.json();
      console.log('📋 Upload response data:', result);
    } else {
      // Handle non-JSON responses (might be HTML error pages)
      const textResponse = await response.text();
      console.error('❌ Non-JSON response:', textResponse.substring(0, 500));
      throw new Error('Server returned non-JSON response. Check server logs.');
    }

    if (!response.ok) {
      const errorMessage = result?.error || result?.message || `HTTP ${response.status}: ${response.statusText}`;
      console.error('❌ Upload failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorMessage,
        details: result?.details
      });
      throw new Error(errorMessage);
    }

    if (!result.success) {
      throw new Error(result.error || 'Upload failed');
    }

    // Success - add to uploaded files
    const newFile = {
      id: uuidv4(),
      name: file.name,
      size: file.size,
      type: file.type,
      url: result.fileUrl,
      gcpPath: result.gcpPath,
      uploadedAt: result.uploadedAt || new Date().toISOString()
    };
    
    setUploadedFiles(prev => [...prev, newFile]);
    
    console.log('✅ File uploaded successfully:', {
      fileName: file.name,
      fileId: newFile.id,
      hasUrl: !!result.fileUrl
    });

  } catch (error) {
    console.error('❌ Upload error details:', {
      errorMessage: error.message,
      errorStack: error.stack,
      fileName: file.name,
      fileSize: file.size
    });
    
    // Enhanced error messaging for users
    let userMessage = `Failed to upload ${file.name}`;
    
    if (error.message.includes('Server returned non-JSON response')) {
      userMessage += '. Server configuration error - please try again later.';
    } else if (error.message.includes('Network request failed') || error.message.includes('fetch')) {
      userMessage += '. Network error - please check your connection and try again.';
    } else if (error.message.includes('413') || error.message.includes('too large')) {
      userMessage += '. File is too large (max 10MB).';
    } else if (error.message.includes('401') || error.message.includes('authentication')) {
      userMessage += '. Authentication error - please refresh and try again.';
    } else if (error.message.includes('403') || error.message.includes('permission')) {
      userMessage += '. Permission denied - please contact support.';
    } else {
      userMessage += `. ${error.message}`;
    }
    
    alert(userMessage);
    
  } finally {
    setUploading(false);
  }
};

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Form validation
  const validateForm = () => {
    if (!materialForm.Name.trim()) {
      alert('Material name is required');
      return false;
    }
    
    if (!materialForm.Description.trim()) {
      alert('Material description is required');
      return false;
    }

    if (!materialForm.QuantityType) {
      alert('Quantity type is required');
      return false;
    }

    if (!materialForm.CategoryId) {
      alert('Category is required');
      return false;
    }

    // Check if at least one environmental value is provided
    const hasEnvironmentalData = Object.values(environmentalData).some(data => 
      data.value && parseFloat(data.value) !== 0 // Changed to !== 0 to allow negative values
    );

    if (!hasEnvironmentalData) {
      alert('Please provide at least one environmental impact value (Carbon, Biogenic, Weight, or Cost)');
      return false;
    }

    return true;
  };

  // Submit material request
  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // ✅ GET FIREBASE TOKEN
      const token = await auth.currentUser.getIdToken();

      const requestData = {
        material: materialForm,
        environmentalData: environmentalData,
        uploadedFiles: uploadedFiles,
        regionId: selectedRegion,
        regionName: regionName,
        visibility: visibility, 
        submittedBy: {
          uid: auth.currentUser?.uid || 'unknown',
          email: auth.currentUser?.email || 'unknown',
          role: role,
        },
        submittedAt: new Date().toISOString(),
      };

      const response = await fetch(`${API_BASE_URL}/material-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-region': selectedRegion,
          'X-User-ID': auth.currentUser?.uid || 'unknown',
          'X-User-Email': auth.currentUser?.email || 'unknown',
          'X-User-Role': role,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error submitting material request:', errorText);
        alert(`Error: ${errorText}`);
        return;
      }

      const result = await response.json();
      
      // Show success modal instead of text message
      setSubmittedRequestId(result.requestId);
      setShowSuccessModal(true);
      
      // Reset form
      setMaterialForm({
        Id: uuidv4(),
        Name: '',
        Description: '',
        ColorHex: '#000000',
        QuantityType: '',
        CategoryId: '',
      });
      
      setEnvironmentalData({
        carbon: { value: '', unit: '' },
        biogenic: { value: '', unit: '' },
        weight: { value: '', unit: '' },
        cost: { value: '', unit: '' }
      });
      
      setUploadedFiles([]);

      logAction(
        auth.currentUser?.uid || 'unknown',
        auth.currentUser?.email || 'unknown',
        'SUBMIT_SUPPLIER_MATERIAL_REQUEST',
        {
          requestId: result.requestId,
          materialName: materialForm.Name,
          regionId: selectedRegion,
          role: role,
          filesCount: uploadedFiles.length
        }
      );

    } catch (error) {
      console.error('Error submitting material request:', error);
      alert('An error occurred while submitting the material request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setSubmittedRequestId('');
  };

  // Redirect non-suppliers
if (role && role !== 'supplier' && role !== 'admin' && role !== 'user') {
    return (
      <div className="rate-builder-page">
        <h1>Access Denied</h1>
        <p>This page is only accessible to suppliers. Your current role: {role}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rate-builder-page">
        <h1>Material Requests</h1>
        <div className="region-info">
          <span className="badge badge--region">Region: {regionName}</span>
          <span className="badge badge--role">
            {role === 'user' ? 'User' : role === 'supplier' ? 'Supplier' : role === 'admin' ? 'Admin' : role}
          </span>
        </div>
        <div>Loading data for {regionName}...</div>
      </div>
    );
  }

  const qtyTypeOptions = quantityTypes.map(t => ({ value: t.id, label: t.name }));
  const categoryOptions = materialCategories.map(c => ({ value: c.Categories, label: c.Categories }));

  return (
    <div className="material-requests-page">
      <div className="page-title-section">
        <h1>Submit Product for Approval</h1>
        <div className="region-info">
          <span className="badge badge--region">Region: {regionName}</span>
          <span className="badge badge--role">
            {role === 'user' ? 'User' : role === 'supplier' ? 'Supplier' : role === 'admin' ? 'Admin' : role || 'Unknown'}
          </span>
        </div>
        <p className="page-description">
          Submit your product with environmental impact data and supporting documents for admin review and approval.
        </p>
      </div>

      <form onSubmit={handleSubmitRequest}>
        {/* Material Information Section */}
        <div className="form-section centered-form">
          <h2>Product Information</h2>
          <div className="form-container">
            {/* Hidden GUID field */}
            <input type="hidden" name="Id" value={materialForm.Id} />

            <div className="input-group">
              <label htmlFor="Name">Product Name *</label>
              <input
                type="text"
                name="Name"
                value={materialForm.Name}
                onChange={handleMaterialInputChange}
                placeholder="Enter material name"
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="Description">Description *</label>
              <div className="textarea-container">
                <textarea
                  name="Description"
                  value={materialForm.Description}
                  onChange={handleMaterialInputChange}
                  placeholder="Enter detailed material description including specifications, composition, and key properties..."
                  rows={5}
                  required
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="ColorHex">Color</label>
              <div className="color-input-group">
                <input
                  type="color"
                  name="ColorHex"
                  value={materialForm.ColorHex}
                  onChange={handleMaterialInputChange}
                />
                <input
                  type="text"
                  name="ColorHexText"
                  value={materialForm.ColorHex}
                  onChange={(e) => setMaterialForm({...materialForm, ColorHex: e.target.value})}
                  placeholder="#000000"
                  className="color-hex-input"
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="QuantityType">Quantity Type *</label>
              <div className="dropdown-container">
                <StyledDropdown
                  name="QuantityType"
                  options={qtyTypeOptions}
                  value={materialForm.QuantityType}
                  onChange={(val) =>
                    setMaterialForm((prev) => ({ ...prev, QuantityType: val }))
                  }
                  placeholder="Select a Quantity Type"
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="CategoryId">Category *</label>
              <div className="dropdown-container">
                <StyledDropdown
                  name="CategoryId"
                  options={categoryOptions}
                  value={materialForm.CategoryId}
                  onChange={(val) =>
                    setMaterialForm((prev) => ({ ...prev, CategoryId: val }))
                  }
                  placeholder="Select a Category"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Environmental Impact Data Section */}
        <br></br>
        <div className="form-section">
          <h2>Product Data</h2>
          <p className="section-description">
            Enter the environmental impact, weight, and cost values for your product. Values will be calculated per unit based on your selected quantity type.
          </p>
          <p className="section-description">
            If you are unaware of the values for your material, leave them blank and we will be in contact. 
          </p>
          
          <div className="environmental-grid">
            <div className="environmental-item">
              <label htmlFor="carbon">Co2eq</label>
              <div className="environmental-input">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={environmentalData.carbon.value}
                  onChange={(e) => handleEnvironmentalChange('carbon', e.target.value)}
                  placeholder="0.00"
                />
                <span className="unit-label">{environmentalData.carbon.unit}</span>
              </div>
            </div>

            <div className="environmental-item biogenic-carbon">
              <label htmlFor="biogenic">
                Biogenic Carbon 
              </label>
              <div className="environmental-input">
                <input
                  type="number"
                  step="0.01"
                  value={environmentalData.biogenic.value}
                  onChange={(e) => handleEnvironmentalChange('biogenic', e.target.value)}
                  placeholder="-2.50"
                />
                <span className="unit-label">{environmentalData.biogenic.unit}</span>
              </div>
              <div className="field-help-text">
                <small>
                  💡 <strong>Positive values:</strong> Carbon emissions | 
                  <strong>Negative values:</strong> Carbon sequestration
                </small>
              </div>
            </div>

            <div className="environmental-item">
              <label htmlFor="weight">Weight</label>
              <div className="environmental-input">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={environmentalData.weight.value}
                  onChange={(e) => handleEnvironmentalChange('weight', e.target.value)}
                  placeholder="0.00"
                />
                <span className="unit-label">{environmentalData.weight.unit}</span>
              </div>
            </div>

            <div className="environmental-item">
              <label htmlFor="cost">Cost</label>
              <div className="environmental-input">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={environmentalData.cost.value}
                  onChange={(e) => handleEnvironmentalChange('cost', e.target.value)}
                  placeholder="0.00"
                />
                <span className="unit-label">{environmentalData.cost.unit}</span>
              </div>
            </div>
          </div>
        </div>

        {/* File Upload Section */}
        <div className="form-section centered-form">
          <h2>Supporting Documents</h2>
          <p className="section-description">
            Upload EPDs, certificates, technical specifications, or other supporting documents (PDF, DOC, XLS, images, etc. Max 10MB per file)
          </p>
          
          <div 
            className={`file-upload-area clickable-upload ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('fileInput').click()}
          >
            <div className="upload-content">
              <div className="upload-icon">📁</div>
              <p>Click here to upload files or drag and drop</p>
              <span className="upload-subtext">Supported formats: PDF, DOC, XLS, Images</span>
              <input
                id="fileInput"
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileInput}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
              />
            </div>
          </div>

          {uploading && (
            <div className="upload-progress">
              <div className="spinner"></div>
              <span>Uploading file...</span>
            </div>
          )}

          {uploadedFiles.length > 0 && (
            <div className="uploaded-files">
              <h4>Uploaded Files ({uploadedFiles.length})</h4>
              {uploadedFiles.map((file) => (
                <div key={file.id} className="file-item">
                  <div className="file-info">
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                  </div>
                  <button
                    type="button"
                    className="remove-file-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(file.id);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Section */}
        <br></br>
        <div className="form-section">
          <VisibilityToggle value={visibility} onChange={setVisibility} />
          <div className="submit-container">
            <button 
              type="submit" 
              className="submit-request-button-product"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting Request...' : 'Submit Material for Approval'}
            </button>
            <p className="submit-help-text">
              Your product request will be sent to administrators for review. You'll be notified when it's approved or if changes are needed.
            </p>
          </div>
        </div>
      </form>

      {/* Modal at component root level - MOVED OUT OF FORM */}
      <SuccessModal 
        isOpen={showSuccessModal}
        onClose={handleCloseModal}
        requestId={submittedRequestId}
        visibility={visibility} 
      />
    </div>
  );
}

export default MaterialRequestsPage;
